import React, { useState, useRef, useCallback, useMemo, memo } from 'react';
import { Text, Tooltip } from '@chakra-ui/react';
import { Button, useNexus } from '@spotoninc/nexus-react';
import {
  ManageRequestsIcon,
  WarningIcon,
  ReportsIcon,
} from '@spotoninc/nexus-web-assets';
import '../../../styles/reskinned-task-list.css';

// Import SVG images
import completedSvg from '../../../media/images/completed.svg';
import cancelledSvg from '../../../media/images/cancelled.svg';
import inProgressSvg from '../../../media/images/in-progress.svg';
import todoSvg from '../../../media/images/todo.svg';

// Import the TaskDrawer component
import TaskDrawer from './TaskDrawer';
import OldTaskDrawer from '../tasks/TaskDrawer/OldTaskDrawer';
import NewTaskDrawer from '../tasks/TaskDrawer';
import { useFeatureFlagFromContext } from '../../../launchdarkly';
import { getStatusImage as getStatusImageV2 } from '../tasks/tasks.utils';

// Helper functions moved outside component to prevent recreation on each render

// Function to get status image based on status
const getStatusImage = status => {
  switch (status) {
    case 'Completed':
      return completedSvg;
    case 'Cancelled':
      return cancelledSvg;
    case 'In Progress':
      return inProgressSvg;
    default:
      return todoSvg;
  }
};

// Function to truncate text if it's too long
const truncateText = (text, maxLength = 25) => {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Reskinned Task List component using Nexus design system
 * This component is only rendered when the showReskin feature flag is enabled
 */
const ReskinnedTaskList = ({ tasks, modifyTasks, fetchTasks, onClick }) => {
  const { theme } = useNexus();
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const bodyRef = useRef(null);
  const taskImprovements = useFeatureFlagFromContext('taskImprovements', false);

  // Function to open the drawer with the selected task
  const openTaskDrawer = useCallback(task => {
    setSelectedTask(task);
    setIsDrawerOpen(true);
  }, []);

  // Function to close the drawer
  const closeTaskDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Function to determine which icon to show based on the header content
  const getHeaderIcon = useCallback(
    header => {
      if (!header || typeof header !== 'string') {
        // Return default icon if header is null, undefined, or not a string
        return (
          <ManageRequestsIcon
            size="1.25rem"
            color={theme.tokens.semanticColorCategorical08}
          />
        );
      }

      const headerText = header.toLowerCase();

      // Check if header contains the word "error" or "errors"
      if (headerText.includes('error') || headerText.includes('errors')) {
        return (
          <WarningIcon
            size="1.25rem"
            color={theme.tokens.semanticColorCategorical04}
          />
        );
      }
      // Check if header contains the word "summary"
      else if (headerText.includes('summary')) {
        return (
          <ReportsIcon
            size="1.25rem"
            color={theme.tokens.semanticColorCategorical02}
          />
        );
      }
      // Default icon
      else {
        return (
          <ManageRequestsIcon
            size="1.25rem"
            color={theme.tokens.semanticColorCategorical08}
          />
        );
      }
    },
    [
      theme.tokens.semanticColorCategorical02,
      theme.tokens.semanticColorCategorical04,
      theme.tokens.semanticColorCategorical08,
    ]
  );

  // Memoize the task list rendering
  const taskListContent = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return <div className="nexus-no-tasks-message">No tasks available</div>;
    }

    return tasks.map(item => (
      <div key={item.id} className="nexus-task-item">
        <div
          className="nexus-task-item-clickable"
          onClick={() => {
            openTaskDrawer(item);
          }}
        >
          <div className="nexus-task-item-content">
            {getHeaderIcon(taskImprovements ? item?.task_type : item.header)}
            <Tooltip
              hasArrow
              label={taskImprovements ? item?.task_title : item.header}
              placement="top"
            >
              <span>
                {truncateText(
                  taskImprovements ? item?.task_title : item.header
                )}
              </span>
            </Tooltip>
          </div>
          <Tooltip hasArrow label={item.status} placement="top">
            <div className="nexus-task-status-icon">
              <img
                src={
                  taskImprovements
                    ? getStatusImageV2(item.status)
                    : getStatusImage(item.status)
                }
                alt={item.status}
                className="nexus-task-status-image"
              />
            </div>
          </Tooltip>
        </div>
      </div>
    ));
  }, [tasks, getHeaderIcon, openTaskDrawer]);

  return (
    <>
      <div className="nexus-task-list-container">
        <div className="nexus-task-list-header">
          <div className="nexus-task-list-header-content">
            <div className="nexus-task-list-title-container">
              <Text className="nexus-task-list-title" whiteSpace="nowrap">
                Pending Tasks
              </Text>
            </div>
            <Button
              variant="text"
              size="small"
              className="nexus-view-all-button"
              onClick={() => onClick && onClick('task')}
            >
              View all
            </Button>
          </div>
        </div>

        <div className="nexus-task-list-body" ref={bodyRef}>
          {taskListContent}
        </div>

        {/* Use the separated TaskDrawer component */}
        {taskImprovements ? (
          <>
            <OldTaskDrawer
              selectedTask={selectedTask}
              isDrawerOpen={selectedTask?.version === '1' && isDrawerOpen}
              onClose={closeTaskDrawer}
              modifyTasks={modifyTasks}
              fetchTasks={fetchTasks}
              isFromTaskTable={false}
            />
            <NewTaskDrawer
              selectedTask={selectedTask}
              isDrawerOpen={selectedTask?.version === '2' && isDrawerOpen}
              onClose={closeTaskDrawer}
              modifyTasks={modifyTasks}
              fetchTasks={fetchTasks}
            />
          </>
        ) : (
          <TaskDrawer
            selectedTask={selectedTask}
            isDrawerOpen={isDrawerOpen}
            onClose={closeTaskDrawer}
            modifyTasks={modifyTasks}
            fetchTasks={fetchTasks}
          />
        )}
      </div>

      {/* Toast notifications are handled by the NexusProvider */}
    </>
  );
};

export default memo(ReskinnedTaskList);
